import { Container, IDestroyOptions, Sprite, TextStyle, Texture, Text } from "pixi.js";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { HourlyBonusSectionType } from "./HourlyBonusSectionType";
import { TextWithTime } from "../../../../../common/components/TextWithTime";
import { HourlyBonusSectionViewDescription } from "./HourlyBonusSectionViewDescription";
import { GameFont } from "../../../../../common/GameFont";
import { HourlyBonusItemData } from "../../../model/HourlyBonusItemData";
import { IFortuneWheelModel } from "../../../model/IFortuneWheelModel";
import { nameof } from "bindable-data";
import { gsap } from "gsap";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { HourlyBonusSectionAnimation } from "./HourlyBonusSectionAnimation";
type Timeline = gsap.core.Timeline;

export class HourlyBonusSection extends Container {
    private static readonly COLLECT_TEXT = "COLLECT";
    private static readonly FREE_SPIN_TEXT_REWARD = "FREE\nSPIN";

    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly hourlyBonusSectionViewDescription: HourlyBonusSectionViewDescription;
    private readonly canvasScaler: ICanvasScaler;
    private hourlyBonusItemData: HourlyBonusItemData;
    private background: Sprite;
    private frame: Sprite;
    private reward: Container;
    private rewardCount: Text;
    private freeSpinText: Text;
    private isSelected: boolean;
    private infoText: TextWithTime;
    private switchFrameAnimation: Timeline;
    private shaderAnimation: HourlyBonusSectionAnimation;

    public constructor(
        wheelResources: HourlyBonusResourcePackage,
        fortuneWheelModel: IFortuneWheelModel,
        hourlyBonusSectionViewDescription: HourlyBonusSectionViewDescription,
        hourlyBonusItemData: HourlyBonusItemData,
        canvasScaler: ICanvasScaler
    ) {
        super();
        this.wheelResources = wheelResources;
        this.fortuneWheelModel = fortuneWheelModel;
        this.hourlyBonusSectionViewDescription = hourlyBonusSectionViewDescription;
        this.hourlyBonusItemData = hourlyBonusItemData;
        this.canvasScaler = canvasScaler;
        this.isSelected = this.hourlyBonusItemData.hourlyBonusSection === this.fortuneWheelModel.currentHourlyBonusSection.value;
        this.switchFrameAnimation = new gsap.core.Timeline();
        this.build();
        this.fortuneWheelModel.propertyChanged.add(this.onFortuneWheelModelPropertyChanged, this);
        this.on("pointerdown", this.hourlyBonusSectionClickHandle, this);
        this.updateShader();
        this.updateSection(true);
    }

    private async onFortuneWheelModelPropertyChanged(model: IFortuneWheelModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "spinAvailableIn"):
                this.infoText.updateTime(model.spinAvailableIn.value);
                break;
        }
    }

    private build(): void {
        this.background = new Sprite(Texture.EMPTY);
        this.background.anchor.set(0.5);
        this.addChild(this.background);
        this.frame = new Sprite(Texture.EMPTY);
        this.frame.anchor.set(0.5);
        this.addChild(this.frame);
        this.createReward();
        this.infoText = new TextWithTime(0, "", HourlyBonusSection.COLLECT_TEXT, new HourlyBonusTextStyle());
        this.infoText.anchor.set(0.5);
        this.infoText.updateTime(this.fortuneWheelModel.spinAvailableIn.value);
        this.addChild(this.infoText);
        this.shaderAnimation = new HourlyBonusSectionAnimation(this.canvasScaler, this.hourlyBonusSectionViewDescription.type, this.wheelResources);
        this.shaderAnimation.eventMode = "none";
        this.shaderAnimation.visible = false;
        this.addChild(this.shaderAnimation);
    }

    private createReward(): void {
        this.reward = new Container();
        this.addChild(this.reward);
        if (this.hourlyBonusItemData.type === "FREE_DICE_ROLLS") {
            const icon = new Sprite(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_dice_rewards_png);
            icon.anchor.set(0.5);
            this.reward.addChild(icon);
            this.rewardCount = new Text(`X${this.hourlyBonusItemData?.freeDiceRollsReward}`, new HourlyBonusTextStyle());
            this.rewardCount.anchor.set(0.5);
            this.rewardCount.position.set(50, 25);
            this.rewardCount.skew._x = -0.087;
            this.rewardCount.angle = -5;
            this.reward.addChild(this.rewardCount);
        } else {
            this.freeSpinText = new Text(HourlyBonusSection.FREE_SPIN_TEXT_REWARD, new FreeSpinGreenRewardTextStyle());
            this.freeSpinText.anchor.set(0.5);
            this.freeSpinText.skew._x = -0.087;
            this.freeSpinText.angle = -5;
            this.reward.addChild(this.freeSpinText);
        }
    }

    private async hourlyBonusSectionClickHandle(): Promise<void> {
        this.infoText.visible = false;
    }

    public updateData(hourlyBonusItemData: HourlyBonusItemData): void {
        this.hourlyBonusItemData = hourlyBonusItemData;
        this.isSelected = this.hourlyBonusItemData?.hourlyBonusSection === this.fortuneWheelModel.currentHourlyBonusSection.value;
        this.updateShader();
        this.updateSection();
    }

    private updateSection(force: boolean = false): void {
        this.eventMode = this.isSelected && this.fortuneWheelModel.spinAvailableIn.value == 0 ? "static" : "none";
        this.infoText.position.set(0, 45);
        switch (this.hourlyBonusSectionViewDescription.type) {
            case HourlyBonusSectionType.left: this.updateWithLeftType(force); break;
            case HourlyBonusSectionType.middle: this.updateWithMiddleType(force); break;
            case HourlyBonusSectionType.right: this.updateWithRightType(force); break;
        }
        this.frame.position.y = this.isSelected && this.fortuneWheelModel.spinAvailableIn.value > 0 ? -5 : 0;
        if (this.isSelected) {
            this.infoText.visible = true;
            this.reward.position.y = -30;
        } else {
            this.infoText.visible = false;
            this.reward.position.y = 0;
        }
        if (this.rewardCount != null) {
            this.rewardCount.text = `X${this.hourlyBonusItemData?.freeDiceRollsReward}`;
        }
    }

    private updateWithLeftType(force: boolean): void {
        if (this.isSelected) {
            this.fortuneWheelModel.spinAvailableIn.value > 0 ?
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_waiting_left_png, force) :
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_left_png, force);
        } else {
            this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_complete_left_png, force);
        }
        this.reward.position.x = -10;
    }

    private updateWithMiddleType(force: boolean): void {
        this.background.texture = this.wheelResources.assets_views_hourly_bonus_hourly_bonus_emty_middle_png;
        if (this.isSelected) {
            this.fortuneWheelModel.spinAvailableIn.value > 0 ?
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_waiting_middle_png, force) :
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_midle_png, force);
        } else {
            this.hourlyBonusItemData.hourlyBonusSection < this.fortuneWheelModel.currentHourlyBonusSection.value ?
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_complete_midle_png, force) :
                this.switchFrameTextureAnimation(Texture.EMPTY, force);
        }
        this.reward.position.x = 0;
    }

    private updateWithRightType(force: boolean): void {
        this.background.texture = this.wheelResources.assets_views_hourly_bonus_hourly_bonus_empty_right_png;
        if (this.isSelected) {
            this.fortuneWheelModel.spinAvailableIn.value > 0 ?
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_waiting_right_png, force) :
                this.switchFrameTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_right_png, force);
        } else {
            this.switchFrameTextureAnimation(Texture.EMPTY, force);
        }
        this.freeSpinText.style = this.isSelected && this.fortuneWheelModel.spinAvailableIn.value == 0 ?
            new FreeSpinWhiteRewardTextStyle() :
            new FreeSpinGreenRewardTextStyle();
        this.reward.position.x = 10;
        this.infoText.position.set(10, 50);
    }

    private async switchFrameTextureAnimation(texture: Texture, force: boolean): Promise<void> {
        if (force || this.frame.texture == texture) {
            this.frame.texture = texture;
            return;
        }
        const previousSection = this.fortuneWheelModel.currentHourlyBonusSection.value - 1;
        if (this.isSelected || this.hourlyBonusItemData.hourlyBonusSection == previousSection) {
            this.switchFrameAnimation.to(this.frame, { alpha: 0.3, duration: 0.15 });
            this.switchFrameAnimation.set(this.frame, { texture: texture });
            this.switchFrameAnimation.to(this.frame, { alpha: 1, duration: 0.15 });
            await this.switchFrameAnimation.play();
            this.switchFrameAnimation.clear();
        }
    }

    private updateShader(): void {
        const isWheelSection = this.hourlyBonusSectionViewDescription.type == HourlyBonusSectionType.right;
        const timerIsOver = this.fortuneWheelModel.spinAvailableIn.value == 0;
        if (this.isSelected) {
            this.shaderAnimation.visible = isWheelSection || timerIsOver;
            if (timerIsOver) {
                this.shaderAnimation.setActiveColor();
            }
            if (timerIsOver || isWheelSection) {
                this.shaderAnimation.playAnimation();
            }
        } else {
            this.shaderAnimation.visible = false;
        }
    }

    public destroyCollectAnimation(): void {
        this.shaderAnimation.destroy();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.fortuneWheelModel.propertyChanged.remove(this.onFortuneWheelModelPropertyChanged, this);
        this.off("pointerdown", this.hourlyBonusSectionClickHandle, this);
        this.switchFrameAnimation.kill();
        super.destroy(options);
    }
}

class HourlyBonusTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 44,
            leading: 5,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.9,
            dropShadowBlur: 5
        }));
    }
}

class FreeSpinGreenRewardTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#a8fc09",
                "#21d214"
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 62,
            leading: -5,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.9,
            dropShadowBlur: 5
        }));
    }
}

class FreeSpinWhiteRewardTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#ffffff",
                "#ffffff"
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 62,
            leading: -5,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.9,
            dropShadowBlur: 5
        }));
    }
}
